/* eslint-disable default-case */
import React, { useState, createRef, useEffect, useRef, useMemo, memo } from "react";
import { observer } from "mobx-react";
import ToggleBtnType from "Components/common/ToggleBtnType";
import {
  filterComponentData,
  companyFilterData,
  contactFilterData,
  DEFAULT_EXPERIENCE_DURATION
} from "./filterComponentData";
import { dataSearchState } from "../state";
import InputBox from "Components/Register/InputBox";
import { AddRemoveValue } from "./AddRemoveValue";
import { CustomMultiSelect } from "./CustomMultiSelect";
import { AutoCompleteMultiSelectDropdown } from "./AutoCompleteMultiSelectDropdown";
import CustomDropdownWithSubmenu from "./CustomDropdownWithSubmenu";
import JobFunctionDropdown from "./JobFunctionDropdown";
import FundingDropdown from "./FundingDropdown";
import { FUNDING_CATEGORIES } from "./fundingData";
import EmployeesByDepartmentInputs from "./EmployeesByDepartmentInputs";
import GrowthByDepartmentInputs from "./GrowthByDepartmentInputs";
import FoundedYearInputs from "./FoundedYearInputs";
import ArrayUtils from "Utils/ArrayUtils";
import Utils from "Utils/utils";
import { verifyBulkActionPerformedAndAllow, isValidDomain } from "../DataSearchUtils";
import { MXP_EVENT, PAGE_TYPE } from "Static/MixPanelEvents";
import makeApi, { URLS } from "Utils/apiURL";
import AutoCompleteDropdown from "Components/common/AutoCompleteDropdown";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { toasterState } from "Components/common/toaster";
import Tooltip from "Components/common/Tooltip";
import { ReactComponent as EditIcon } from "Assets/svg/icn-rename1.svg";
import { ReactComponent as DeleteIconDiscover } from "Assets/svg/icn-delete1.svg";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const MAX_COMPANY_DOMAIN_NAME_ITEMS = 5;
const MAX_CSV_LIST_RECORDS = 100;
const CSV_RULES_TOOLTIP = "One column, no header. Max 100 records.";

const parseCsvToOneColumn = text => {
  const lines = String(text || "").split(/\r?\n/).map(l => {
    const firstCol = l.trim().split(",")[0];
    return firstCol ? firstCol.trim() : "";
  }).filter(Boolean);
  return lines;
};

const CompanyDomainNameInputs = observer(({ inputBoxRef }) => {
  const [domainValue, setDomainValue] = useState("");
  const [nameValue, setNameValue] = useState("");
  const [domainError, setDomainError] = useState(false);
  const [listUploading, setListUploading] = useState(false);
  const searchType = dataSearchState?.filterComponentData?.companySearchType || "domain";
  const companyDomain = dataSearchState?.filterComponentData?.companyDomain || [];
  const companyName = dataSearchState?.filterComponentData?.companyName || [];
  const companyDomainList = dataSearchState?.filterComponentData?.companyDomainList || [];
  const companyNameList = dataSearchState?.filterComponentData?.companyNameList || [];

  const setSearchType = val => {
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      companySearchType: val
    });
  };

  const handleDomainListFile = e => {
    if (searchType !== "domainList") return;
    const input = e?.target;
    const file = input?.files?.[0];
    if (!file) return;
    setListUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const lines = parseCsvToOneColumn(reader.result);
      if (lines.length > MAX_CSV_LIST_RECORDS) {
        toasterState.setToastMsg("Invalid file.", "fail");
        setListUploading(false);
        if (input) input.value = "";
        return;
      }
      const valid = lines.filter(line => isValidDomain(line));
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        companyDomainList: valid,
        companySearchType: "domainList"
      });
      setListUploading(false);
      if (input) input.value = "";
    };
    reader.readAsText(file);
  };

  const handleNameListFile = e => {
    if (searchType !== "nameList") return;
    const input = e?.target;
    const file = input?.files?.[0];
    if (!file) return;
    setListUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const lines = parseCsvToOneColumn(reader.result);
      if (lines.length > MAX_CSV_LIST_RECORDS) {
        toasterState.setToastMsg("Invalid file.", "fail");
        setListUploading(false);
        if (input) input.value = "";
        return;
      }
      const valid = lines.filter(Boolean);
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        companyNameList: valid,
        companySearchType: "nameList"
      });
      setListUploading(false);
      if (input) input.value = "";
    };
    reader.readAsText(file);
  };

  const addDomain = () => {
    const v = domainValue.trim();
    if (!v) return;
    if (!isValidDomain(v)) {
      setDomainError(true);
      return;
    }
    setDomainError(false);
    if (companyDomain.length >= MAX_COMPANY_DOMAIN_NAME_ITEMS) return;
    if (companyDomain.length > 0 && companyDomain.includes(v)) {
      setDomainValue("");
      return;
    }
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      companyDomain: [...companyDomain, v]
    });
    setDomainValue("");
  };

  const addName = () => {
    const v = nameValue.trim();
    if (!v) return;
    if (companyName.length >= MAX_COMPANY_DOMAIN_NAME_ITEMS) return;
    if (companyName.length > 0 && companyName.includes(v)) {
      setNameValue("");
      return;
    }
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      companyName: [...companyName, v]
    });
    setNameValue("");
  };

  return (
    <div className="companyDomainNameSection">
      <div className="filterComponentInputHeader companyDomainNameInputsStack">
        <div className={`companyDomainNameInputWrap ${domainError ? "companyDomainNameInputWrap--error" : ""}`}>
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="companySearchType"
              checked={searchType === "domain"}
              onChange={() => setSearchType("domain")}
            />
            <span>Company Domain</span>
          </label>
          <InputBox
            name="companyDomainInput"
            id="companyDomainInput"
            type="text"
            value={domainValue}
            changeAction={(val) => { setDomainValue(val); setDomainError(false); }}
            keyupAction={e => Utils.isSubmitKeyAction(e) && addDomain()}
            label=""
            text=""
            placeholder="e.g. example.com"
            inputBoxRef={inputBoxRef}
            focus={true}
            readOnly={searchType !== "domain"}
          />
        </div>
        <div className="companyDomainNameInputWrap">
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="companySearchType"
              checked={searchType === "name"}
              onChange={() => setSearchType("name")}
            />
            <span>Company Name</span>
          </label>
          <InputBox
            name="companyNameInput"
            id="companyNameInput"
            type="text"
            value={nameValue}
            changeAction={(val) => setNameValue(val)}
            keyupAction={e => Utils.isSubmitKeyAction(e) && addName()}
            label=""
            text=""
            placeholder="e.g. Acme Inc"
            readOnly={searchType !== "name"}
          />
        </div>
        <div className="companyDomainNameInputWrap">
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="companySearchType"
              checked={searchType === "domainList"}
              onChange={() => setSearchType("domainList")}
            />
            <span>Company Domain List</span>
          </label>
          <div className={`companyDomainNameFileUpload ${searchType !== "domainList" ? "companyDomainNameFileUpload--disabled" : ""}`}>
            <input
              type="file"
              accept=".csv,.txt"
              onChange={handleDomainListFile}
              className="companyDomainNameFileInput"
              id="companyDomainListFile"
              disabled={searchType !== "domainList"}
            />
            <label
              htmlFor={searchType === "domainList" ? "companyDomainListFile" : undefined}
              className="companyDomainNameFileLabel"
              onClick={searchType !== "domainList" ? ev => ev.preventDefault() : undefined}
            >
              {listUploading ? "Uploading…" : "Upload CSV"}
            </label>
            <Tooltip tooltip={CSV_RULES_TOOLTIP} placement="top">
              <span className="companyDomainNameFileInfoIcon material-icons" aria-label="CSV rules">info</span>
            </Tooltip>
            {searchType === "domainList" && companyDomainList.length > 0 && (
              <span className="companyDomainNameListDetected">{companyDomainList.length} domains detected</span>
            )}
          </div>
        </div>
        <div className="companyDomainNameInputWrap">
          <label className="companyDomainNameFieldLabel">
            <input
              type="radio"
              name="companySearchType"
              checked={searchType === "nameList"}
              onChange={() => setSearchType("nameList")}
            />
            <span>Company Name List</span>
          </label>
          <div className={`companyDomainNameFileUpload ${searchType !== "nameList" ? "companyDomainNameFileUpload--disabled" : ""}`}>
            <input
              type="file"
              accept=".csv,.txt"
              onChange={handleNameListFile}
              className="companyDomainNameFileInput"
              id="companyNameListFile"
              disabled={searchType !== "nameList"}
            />
            <label
              htmlFor={searchType === "nameList" ? "companyNameListFile" : undefined}
              className="companyDomainNameFileLabel"
              onClick={searchType !== "nameList" ? ev => ev.preventDefault() : undefined}
            >
              {listUploading ? "Uploading…" : "Upload CSV"}
            </label>
            <Tooltip tooltip={CSV_RULES_TOOLTIP} placement="top">
              <span className="companyDomainNameFileInfoIcon material-icons" aria-label="CSV rules">info</span>
            </Tooltip>
            {searchType === "nameList" && companyNameList.length > 0 && (
              <span className="companyDomainNameListDetected">{companyNameList.length} names detected</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

const EXPERIENCE_DURATION_TOOLTIP = "Enter minimum and/or maximum duration. Months 0–12. If only min is set, that criteria applies. Maximum must be ≥ minimum when both are set. Values save on Enter or when you leave a field.";

const MONTHS_MAX = 12;
const EXPERIENCE_KEYS = Object.keys(DEFAULT_EXPERIENCE_DURATION);

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const FUNDING_DATE_YEAR_MIN = 2000;
const FUNDING_DATE_YEAR_MAX = new Date().getFullYear();
const FUNDING_DATE_YEARS = Array.from({ length: FUNDING_DATE_YEAR_MAX - FUNDING_DATE_YEAR_MIN + 1 }, (_, i) => FUNDING_DATE_YEAR_MAX - i);

const renderFundingDateHeader = ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
  <div className="fundingDatePickerHeader">
    <button type="button" className="fundingDatePickerNav" onClick={decreaseMonth} disabled={prevMonthButtonDisabled} aria-label="Previous month">
      <span className="material-icons">chevron_left</span>
    </button>
    <div className="fundingDatePickerMonthYear">
      <select
        className="fundingDatePickerMonthSelect"
        value={date.getMonth()}
        onChange={e => changeMonth(Number(e.target.value))}
        aria-label="Month"
      >
        {MONTH_NAMES.map((name, i) => (
          <option key={name} value={i}>{name}</option>
        ))}
      </select>
      <select
        className="fundingDatePickerYearSelect"
        value={date.getFullYear()}
        onChange={e => changeYear(Number(e.target.value))}
        aria-label="Year"
      >
        {FUNDING_DATE_YEARS.map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
    <button type="button" className="fundingDatePickerNav" onClick={increaseMonth} disabled={nextMonthButtonDisabled} aria-label="Next month">
      <span className="material-icons">chevron_right</span>
    </button>
  </div>
);

const experienceSectionConfig = [
  {
    sectionKey: "total",
    title: "Total Experience",
    minYearKey: "experienceTotalMinYear",
    maxYearKey: "experienceTotalMaxYear",
    minMonthKey: "experienceTotalMinMonth",
    maxMonthKey: "experienceTotalMaxMonth"
  },
  {
    sectionKey: "currentCompany",
    title: "Time in Current Company",
    minYearKey: "experienceCurrentCompanyMinYear",
    maxYearKey: "experienceCurrentCompanyMaxYear",
    minMonthKey: "experienceCurrentCompanyMinMonth",
    maxMonthKey: "experienceCurrentCompanyMaxMonth"
  },
  {
    sectionKey: "currentJob",
    title: "Time in Current Role",
    minYearKey: "experienceCurrentJobMinYear",
    maxYearKey: "experienceCurrentJobMaxYear",
    minMonthKey: "experienceCurrentJobMinMonth",
    maxMonthKey: "experienceCurrentJobMaxMonth"
  }
];

const parseNum = v => {
  if (v === "" || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};
const toNum = parseNum;

const ExperienceDurationInputs = ({ experienceValues }) => {
  const [local, setLocal] = useState(() =>
    EXPERIENCE_KEYS.reduce((acc, k) => {
      const v = experienceValues[k] ?? 0;
      acc[k] = v === 0 ? "" : String(v);
      return acc;
    }, {})
  );
  const [errors, setErrors] = useState({});
  const prevValuesRef = useRef(null);

  useEffect(() => {
    const prev = prevValuesRef.current;
    const same = prev && EXPERIENCE_KEYS.every(k => (prev[k] ?? 0) === (experienceValues[k] ?? 0));
    if (same) return;
    prevValuesRef.current = { ...experienceValues };
    setLocal(
      EXPERIENCE_KEYS.reduce((acc, k) => {
        const v = experienceValues[k] ?? 0;
        acc[k] = v === 0 ? "" : String(v);
        return acc;
      }, {})
    );
    setErrors({});
    dataSearchState.setExperienceDurationErrors({});
  }, [experienceValues]);

  useEffect(() => {
    dataSearchState.setExperienceDurationErrors(errors);
  }, [errors]);

  const handleChange = (key, value) => {
    setLocal(prev => ({ ...prev, [key]: value }));
  };

  const validate = (minY, minM, maxY, maxM) => {
    if (minM > MONTHS_MAX || maxM > MONTHS_MAX) return "Months must be between 0 and 12.";
    const minTotal = minY * 12 + minM;
    const maxTotal = maxY * 12 + maxM;
    if (maxTotal > 0 && maxTotal < minTotal) return "Maximum must be greater than or equal to minimum.";
    return null;
  };

  const commitSection = (sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey) => {
    const minY = parseNum(local[minYearKey]);
    const minM = parseNum(local[minMonthKey]);
    const maxY = parseNum(local[maxYearKey]);
    const maxM = parseNum(local[maxMonthKey]);
    const err = validate(minY, minM, maxY, maxM);
    setErrors(prev => {
      const next = { ...prev, [sectionKey]: err };
      dataSearchState.setExperienceDurationErrors(next);
      return next;
    });
    if (err) return;
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      [minYearKey]: minY,
      [minMonthKey]: minM,
      [maxYearKey]: maxY,
      [maxMonthKey]: maxM
    });
  };

  const onBlur = (sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey) => {
    requestAnimationFrame(() => commitSection(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey));
  };

  const onKeyDown = (e, sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitSection(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey);
    }
  };

  return (
    <div className="experienceDurationSection">
      {experienceSectionConfig.map(({ sectionKey, title, minYearKey, maxYearKey, minMonthKey, maxMonthKey }) => (
        <div key={sectionKey} className="experienceDurationBlock">
          <div className="experienceDurationBlockHeader">
            <span className="experienceDurationBlockTitle">{title}</span>
            <Tooltip tooltip={EXPERIENCE_DURATION_TOOLTIP} placement="top">
              <span className="experienceDurationBlockInfoIcon material-icons" aria-label="Duration rules">info</span>
            </Tooltip>
          </div>
          <div className="experienceDurationGrid">
            <div className="experienceDurationColLabel experienceDurationColYears">Years</div>
            <div className="experienceDurationColDash" aria-hidden="true" />
            <div className="experienceDurationColLabel experienceDurationColMonths">Months</div>

            <div className="experienceDurationCell experienceDurationYearCell">
              <input
                type="number"
                min={0}
                placeholder="Min Years"
                className="experienceDurationInput"
                value={local[minYearKey] ?? ""}
                onChange={e => handleChange(minYearKey, e.target.value)}
                onBlur={() => onBlur(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                onKeyDown={e => onKeyDown(e, sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                aria-label={`${title} min years`}
              />
            </div>
            <div className="experienceDurationCell experienceDurationDashCell">
              <span className="experienceDurationDash">-</span>
            </div>
            <div className="experienceDurationCell experienceDurationMonthCell">
              <input
                type="number"
                min={0}
                max={MONTHS_MAX}
                placeholder="Min Months"
                className="experienceDurationInput experienceDurationInputMonths"
                value={local[minMonthKey] ?? ""}
                onChange={e => handleChange(minMonthKey, e.target.value)}
                onBlur={() => onBlur(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                onKeyDown={e => onKeyDown(e, sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                aria-label={`${title} min months`}
              />
            </div>

            <div className="experienceDurationCell experienceDurationYearCell">
              <input
                type="number"
                min={0}
                placeholder="Max Years"
                className="experienceDurationInput"
                value={local[maxYearKey] ?? ""}
                onChange={e => handleChange(maxYearKey, e.target.value)}
                onBlur={() => onBlur(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                onKeyDown={e => onKeyDown(e, sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                aria-label={`${title} max years`}
              />
            </div>
            <div className="experienceDurationCell experienceDurationDashCell">
              <span className="experienceDurationDash">-</span>
            </div>
            <div className="experienceDurationCell experienceDurationMonthCell">
              <input
                type="number"
                min={0}
                max={MONTHS_MAX}
                placeholder="Max Months"
                className="experienceDurationInput experienceDurationInputMonths"
                value={local[maxMonthKey] ?? ""}
                onChange={e => handleChange(maxMonthKey, e.target.value)}
                onBlur={() => onBlur(sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                onKeyDown={e => onKeyDown(e, sectionKey, minYearKey, maxYearKey, minMonthKey, maxMonthKey)}
                aria-label={`${title} max months`}
              />
            </div>
          </div>
          {errors[sectionKey] && (
            <div className="experienceDurationError" role="alert">
              {errors[sectionKey]}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const experienceValuesEqual = (a, b) =>
  Object.keys(DEFAULT_EXPERIENCE_DURATION).every(k => (a[k] ?? 0) === (b[k] ?? 0));

const ExperienceDurationInputsWrapper = observer(() => {
  const fd = dataSearchState?.filterComponentData || {};
  const experienceValues = useMemo(
    () =>
      Object.keys(DEFAULT_EXPERIENCE_DURATION).reduce((acc, k) => {
        acc[k] = fd[k] ?? 0;
        return acc;
      }, {}),
    [
      fd.experienceCurrentJobMinYear,
      fd.experienceCurrentJobMaxYear,
      fd.experienceCurrentJobMinMonth,
      fd.experienceCurrentJobMaxMonth,
      fd.experienceCurrentCompanyMinYear,
      fd.experienceCurrentCompanyMaxYear,
      fd.experienceCurrentCompanyMinMonth,
      fd.experienceCurrentCompanyMaxMonth,
      fd.experienceTotalMinYear,
      fd.experienceTotalMaxYear,
      fd.experienceTotalMinMonth,
      fd.experienceTotalMaxMonth
    ]
  );
  return <ExperienceDurationInputsMemo experienceValues={experienceValues} />;
});

const ExperienceDurationInputsMemo = memo(ExperienceDurationInputs, (prev, next) =>
  experienceValuesEqual(prev.experienceValues, next.experienceValues)
);

// Funding range inputs: Last Funding Round Date (calendar), Last/Total Amount (min/max $)
const FundingRangeInputs = observer(() => {
  const d = dataSearchState?.filterComponentData || {};
  const durationStart = d.fundingDurationStart != null && d.fundingDurationStart !== ""
    ? new Date(Number(d.fundingDurationStart))
    : null;
  const durationEnd = d.fundingDurationEnd != null && d.fundingDurationEnd !== ""
    ? new Date(Number(d.fundingDurationEnd))
    : null;

  const setDuration = (key, date) => {
    const ts = date ? String(new Date(date).setHours(0, 0, 0, 0)) : null;
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      [key]: ts
    });
  };

  const setAmount = (key, value) => {
    const num = value === "" || value == null ? null : (Number(value) >= 0 ? Number(value) : null);
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      [key]: num
    });
  };

  return (
    <div className="fundingRangeSection">
      <div className="fundingRangeBlock">
        <div className="fundingRangeBlockTitle">Last Funding Round Date</div>
        <div className="fundingRangeRow fundingRangeDateRow">
          <div className="fundingRangeDateWrap fundingDatePickerWrap">
            <ReactDatePicker
              id="fundingDurationFrom"
              selected={durationStart}
              onChange={date => setDuration("fundingDurationStart", date)}
              placeholderText="From"
              dateFormat="MMM d, yyyy"
              isClearable
              className="fundingRangeInput fundingRangeDateInput"
              minDate={new Date(FUNDING_DATE_YEAR_MIN, 0, 1)}
              maxDate={new Date()}
              renderCustomHeader={renderFundingDateHeader}
              popperClassName="fundingDatePickerPopper"
              popperPlacement="bottom-start"
            />
            <span
              className="fundingRangeDateIcon material-icons"
              role="button"
              tabIndex={-1}
              aria-label="Toggle calendar"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                const wrap = e.currentTarget.closest(".fundingRangeDateWrap");
                const input = wrap?.querySelector(".react-datepicker-wrapper input");
                if (!input) return;
                const isInputFocused = document.activeElement === input;
                const popper = document.querySelector(".react-datepicker-popper.fundingDatePickerPopper");
                const isPopperVisible = popper && popper.getBoundingClientRect().height > 0;
                if (isInputFocused || isPopperVisible) {
                  input.blur();
                } else {
                  input.focus();
                }
              }}
            >
              calendar_today
            </span>
          </div>
          <div className="fundingRangeDateWrap fundingDatePickerWrap">
            <ReactDatePicker
              id="fundingDurationTo"
              selected={durationEnd}
              onChange={date => setDuration("fundingDurationEnd", date)}
              placeholderText="To"
              dateFormat="MMM d, yyyy"
              isClearable
              className="fundingRangeInput fundingRangeDateInput"
              minDate={durationStart ? new Date(Math.max(durationStart.getTime(), new Date(FUNDING_DATE_YEAR_MIN, 0, 1).getTime())) : new Date(FUNDING_DATE_YEAR_MIN, 0, 1)}
              maxDate={new Date()}
              renderCustomHeader={renderFundingDateHeader}
              popperClassName="fundingDatePickerPopper"
              popperPlacement="bottom-start"
            />
            <span
              className="fundingRangeDateIcon material-icons"
              role="button"
              tabIndex={-1}
              aria-label="Toggle calendar"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                const wrap = e.currentTarget.closest(".fundingRangeDateWrap");
                const input = wrap?.querySelector(".react-datepicker-wrapper input");
                if (!input) return;
                const isInputFocused = document.activeElement === input;
                const popper = document.querySelector(".react-datepicker-popper.fundingDatePickerPopper");
                const isPopperVisible = popper && popper.getBoundingClientRect().height > 0;
                if (isInputFocused || isPopperVisible) {
                  input.blur();
                } else {
                  input.focus();
                }
              }}
            >
              calendar_today
            </span>
          </div>
        </div>
      </div>
      <div className="fundingRangeBlock">
        <div className="fundingRangeBlockTitle">Last Funding Round Amount</div>
        <div className="fundingRangeRow">
          <div className="fundingRangeAmountWrap">
            <span className="fundingRangeDollar">$</span>
            <input
              type="number"
              min={0}
              placeholder="Min"
              className="fundingRangeInput fundingRangeAmountInput"
              value={d.fundingLastAmountStart ?? ""}
              onChange={e => setAmount("fundingLastAmountStart", e.target.value === "" ? "" : e.target.value)}
              aria-label="Last funding round amount min"
            />
          </div>
          <span className="fundingRangeDash">-</span>
          <div className="fundingRangeAmountWrap">
            <span className="fundingRangeDollar">$</span>
            <input
              type="number"
              min={0}
              placeholder="Max"
              className="fundingRangeInput fundingRangeAmountInput"
              value={d.fundingLastAmountEnd ?? ""}
              onChange={e => setAmount("fundingLastAmountEnd", e.target.value === "" ? "" : e.target.value)}
              aria-label="Last funding round amount max"
            />
          </div>
        </div>
      </div>
      <div className="fundingRangeBlock">
        <div className="fundingRangeBlockTitle">Total Funding Amount</div>
        <div className="fundingRangeRow">
          <div className="fundingRangeAmountWrap">
            <span className="fundingRangeDollar">$</span>
            <input
              type="number"
              min={0}
              placeholder="Min"
              className="fundingRangeInput fundingRangeAmountInput"
              value={d.fundingTotalAmountStart ?? ""}
              onChange={e => setAmount("fundingTotalAmountStart", e.target.value === "" ? "" : e.target.value)}
              aria-label="Total funding amount min"
            />
          </div>
          <span className="fundingRangeDash">-</span>
          <div className="fundingRangeAmountWrap">
            <span className="fundingRangeDollar">$</span>
            <input
              type="number"
              min={0}
              placeholder="Max"
              className="fundingRangeInput fundingRangeAmountInput"
              value={d.fundingTotalAmountEnd ?? ""}
              onChange={e => setAmount("fundingTotalAmountEnd", e.target.value === "" ? "" : e.target.value)}
              aria-label="Total funding amount max"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

const FilterComponentAction = observer(props => {
  const {
    action = "",
    id = "",
    name = "",
    index = "",
    activeIndex = "",
    setActiveIndex = () => { },
    setTableLoading = () => { },
    filterCount = 0
  } = props;

  const [nameValue, setNameValue] = useState("");
  const inputBoxRef = createRef();

  const handleToggleAction = checked => {
    const performAction = () => {
      Utils.mixpanelTrack(MXP_EVENT.SKIP_CONTACTS_CLICKED, {
        pageType: PAGE_TYPE.discover,
        value: checked
      });
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        [id]: checked
      });
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performAction);
    } else {
      performAction();
    }
  };

  const handleInputChange = value => {
    setNameValue(value);
  };

  const handleKeyPress = (event, nameValue) => {
    if (Utils.isSubmitKeyAction(event)) {
      handleInputAdd(nameValue);
    }
  };

  const handleInputAdd = nameValue => {
    if (nameValue?.length > 0) {
      const performAction = () => {
        let tempList = dataSearchState?.filterComponentData?.[id] || [];
        if (tempList?.length < 5) {
          if (tempList?.length > 0) {
            if (ArrayUtils.isUniqueArray(tempList, nameValue)) {
              dataSearchState.setFilterComponentData({
                ...dataSearchState.filterComponentData,
                [id]: [
                  ...new Set([
                    ...dataSearchState?.filterComponentData?.[id],
                    nameValue
                  ])
                ]
              });
            }
          } else {
            dataSearchState.setFilterComponentData({
              ...dataSearchState.filterComponentData,
              [id]: [nameValue]
            });
          }
          setNameValue("");
        }
      };
      if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
        verifyBulkActionPerformedAndAllow(performAction, () =>
          setNameValue("")
        );
      } else {
        performAction();
      }
    } else {
      inputBoxRef && inputBoxRef.current && inputBoxRef.current.focus();
    }
  };

  const getActionComponent = () => {
    switch (action) {
      case "autoCompleteMultiSelectDropdown":
        return (
          <AutoCompleteMultiSelectDropdown
            {...props}
            showSearchPanel={true}
            showDropdown={index === activeIndex}
            setActiveIndex={setActiveIndex}
            showApplybtn={["keywords", "companykeywords", "contactkeywords", "location", "companydomain"].includes(
              id?.toLowerCase()
            )}
          />
        );
      case "jobFunctionDropdown":
        return (
          <JobFunctionDropdown
            {...props}
            showDropdown={index === activeIndex}
            setActiveIndex={setActiveIndex}
            discoverPage={true}
          />
        );
      case "fundingDropdown":
        return (
          <FundingDropdown
            {...props}
            showDropdown={index === activeIndex && (props.fundingTypeDropdownOpen !== false)}
            setActiveIndex={setActiveIndex}
            discoverPage={true}
            onCloseDropdownOnly={props.onCloseFundingTypeDropdown}
          />
        );
      case "multiSelectDropdown":
        return (
          <CustomMultiSelect
            {...props}
            showSearchPanel={false}
            showDropdown={index === activeIndex}
            setActiveIndex={setActiveIndex}
            discoverPage={true}
          />
        );
      case "toggle":
        return (
          <div className="toggleActionSection">
            <ToggleBtnType
              id={id}
              defaultValue={dataSearchState?.filterComponentData?.[id]}
              lastText={""}
              pageType={name}
              isAllowed={true}
              cbk={handleToggleAction}
            />
          </div>
        );
      case "companyDomainName":
        return (
          <CompanyDomainNameInputs inputBoxRef={inputBoxRef} />
        );
      case "experienceDuration":
        return <ExperienceDurationInputsWrapper />;
      case "inputBox":
        const inputObject = {
          name: id,
          id: id,
          type: "text",
          value: nameValue || "",
          changeAction: handleInputChange,
          keyupAction: e => handleKeyPress(e, nameValue),
          label: "",
          text: "",
          placeholder: name,
          inputBoxRef,
          focus: true
        };

        return (
          <>
            <div className="filterComponentInputHeader">
              <InputBox {...inputObject} />
              {filterCount > 0 && (
                <div className="flexCenter">
                  <div className="filterCount">{filterCount}</div>
                </div>
              )}
            </div>
          </>
        );
    }
  };
  return getActionComponent();
});

const FilterComponent = observer(props => {
  const {
    setTableLoading = () => { },
    setShowSavePopup = () => { },
    editCallBack = () => { },
    minimumSelectionMade = false,
    savedListOptions = [],
    setSavedListOptions = () => { },
    onSliderOpenChange = () => { }
  } = props;

  const [checkboxVal, setCheckboxVal] = useState(
    dataSearchState.filterComponentData.titleExactMatch
  );
  const [activeIndex, setActiveIndex] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [savedSearchListLoader, setSavedSearchListLoader] = useState(false);
  const [selectedValueId, setSelectedValueId] = useState("");
  const [activeFilterIndex, setActiveFilterIndex] = useState(null);
  const [fundingTypeDropdownOpen, setFundingTypeDropdownOpen] = useState(false);

  useEffect(() => {
    onSliderOpenChange(activeFilterIndex != null);
  }, [activeFilterIndex, onSliderOpenChange]);

  useEffect(() => {
    if (dataSearchState.closeFilterSliderSignal > 0) {
      setActiveFilterIndex(null);
      setActiveIndex(null);
      onSliderOpenChange(false);
    }
  }, [dataSearchState.closeFilterSliderSignal]);

  useEffect(() => {
    const filterBody = document.getElementById("startSearchingItem");
    if (filterBody) {
      filterBody.scrollTop = 0;
    }
  }, []);

  const setSelectedValueCbk = (str = "") => {
    setSelectedValueId(str);
  };

  const getIndustryFilterCount = (id = "") => {
    let count = 0;
    if (dataSearchState?.industriesList?.length) {
      let tempData = dataSearchState?.industriesList?.forEach(item => {
        const { name = "", mapped_industries = [] } = item || {};
        if (dataSearchState?.filterComponentData?.[id]?.includes(name)) {
          count += mapped_industries?.length || 0;
        }
      });
    } else {
      count = dataSearchState?.filterComponentData?.[id]?.length || 0;
    }
    return (
      count + dataSearchState?.filterComponentData?.["sector"]?.length || 0
    );
  };

  const hasExperienceDurationSet = () => {
    const d = dataSearchState?.filterComponentData || {};
    const keys = Object.keys(DEFAULT_EXPERIENCE_DURATION);
    return keys.some(k => toNum(d[k]) > 0);
  };

  const hasFundingRangeSet = () => {
    const d = dataSearchState?.filterComponentData || {};
    const hasDuration = (d.fundingDurationStart != null && d.fundingDurationStart !== "") || (d.fundingDurationEnd != null && d.fundingDurationEnd !== "");
    const hasLastAmount = d.fundingLastAmountStart != null || d.fundingLastAmountEnd != null;
    const hasTotalAmount = d.fundingTotalAmountStart != null || d.fundingTotalAmountEnd != null;
    return hasDuration || hasLastAmount || hasTotalAmount;
  };

  const getFilterCount = (id = "") => {
    if (id === "experience") return hasExperienceDurationSet() ? 1 : 0;
    if (id === "funding") {
      const typesLen = (dataSearchState?.filterComponentData?.funding || []).length;
      const rangeCount = hasFundingRangeSet() ? 1 : 0;
      return typesLen + rangeCount;
    }
    if (id === "employeesByDepartment") {
      const arr = dataSearchState?.filterComponentData?.employeesByDepartment || [];
      return arr.filter(
        r => (r.function || []).length > 0 && (r.start != null || r.end != null)
      ).length;
    }
    if (id === "growthByDepartment") {
      const arr = dataSearchState?.filterComponentData?.growthByDepartment || [];
      return arr.filter(
        r =>
          (r.function || []).length > 0 &&
          (r.start != null || r.end != null || !!r.timeFrame)
      ).length;
    }
    if (id === "foundedYear") {
      const d = dataSearchState?.filterComponentData || {};
      const start = d.foundedYearStart;
      const end = d.foundedYearEnd;
      return start != null || end != null ? 1 : 0;
    }
    if (id === "growthByDepartment") {
      const arr = dataSearchState?.filterComponentData?.growthByDepartment || [];
      return arr.filter(
        r =>
          (r.function || []).length > 0 &&
          (r.start != null || r.end != null || !!r.timeFrame)
      ).length;
    }
    if (["companydomain", "location", "companylocation", "keywords", "companykeywords", "contactkeywords", "industry", "technologies"].includes(id?.toLowerCase())) {
      const listCount = dataSearchState?.selectedFilteredList?.[id]?.length;
      const dataCount = dataSearchState?.filterComponentData?.[id]?.length;
      if (id?.toLowerCase() === "companydomain") {
        const st = dataSearchState?.filterComponentData?.companySearchType || "domain";
        if (st === "name") return dataSearchState?.filterComponentData?.companyName?.length ?? 0;
        if (st === "domainList") return (dataSearchState?.filterComponentData?.companyDomainList?.length > 0) ? 1 : 0;
        if (st === "nameList") return (dataSearchState?.filterComponentData?.companyNameList?.length > 0) ? 1 : 0;
        return dataSearchState?.filterComponentData?.companyDomain?.length ?? listCount ?? 0;
      }
      return listCount ?? 0;
    } else {
      return dataSearchState?.filterComponentData?.[id]?.length;
    }
  };

  const getActiveFiltersSummary = () => {
    const defaults = getDefaultFilterState();
    const items = [];
    [...companyFilterData, ...contactFilterData].forEach(def => {
      const id = def?.id;
      const name = def?.name;
      const val = dataSearchState?.filterComponentData?.[id];
      const listVal = dataSearchState?.selectedFilteredList?.[id];
      const count = getFilterCount(id);
      if (count === undefined || count === 0) return;
      if (id === "titleExactMatch" || id === "excludeTeamOwns") return;
      items.push({ id, label: name, count });
    });
    return items;
  };

  const clearSingleFilter = (id = "") => {
    const defaults = getDefaultFilterState();
    const defaultVal = defaults[id];
    const performClear = () => {
      const nextFilter = id === "experience"
        ? { ...dataSearchState.filterComponentData, ...DEFAULT_EXPERIENCE_DURATION }
        : { ...dataSearchState.filterComponentData, [id]: defaultVal };
      if (id === "funding") {
        nextFilter.fundingTotalAmountStart = null;
        nextFilter.fundingTotalAmountEnd = null;
        nextFilter.fundingLastAmountStart = null;
        nextFilter.fundingLastAmountEnd = null;
        nextFilter.fundingDurationStart = null;
        nextFilter.fundingDurationEnd = null;
      }
      if (id === "employeesByDepartment") {
        nextFilter.employeesByDepartment = [];
        dataSearchState.setEmployeesByDepartmentErrors({});
      }
      if (id === "growthByDepartment") {
        nextFilter.growthByDepartment = [];
        dataSearchState.setGrowthByDepartmentErrors({});
      }
      if (id === "foundedYear") {
        nextFilter.foundedYearStart = null;
        nextFilter.foundedYearEnd = null;
      }
      if (id?.toLowerCase() === "industry") nextFilter.sector = [];
      if (id?.toLowerCase() === "companydomain") {
        nextFilter.companyName = [];
        nextFilter.companyDomainList = [];
        nextFilter.companyNameList = [];
        nextFilter.companySearchType = "domain";
      }
      dataSearchState.setFilterComponentData(nextFilter);
      if (["companydomain", "location", "companylocation", "companykeywords", "contactkeywords", "industry", "technologies"].includes(id?.toLowerCase())) {
        dataSearchState.setSelectedFilteredList({
          ...dataSearchState.selectedFilteredList,
          [id]: []
        });
      }
      if (id === "level" || id === "confidenceScore" || id === "profileBadge") resetFilterData(id, false);
      // Do not refresh or reset page — just clear selection; stale overlay will prompt user to refresh
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performClear);
    } else {
      performClear();
    }
  };

  const resetFilterData = (id = "", checked = false) => {
    if (id) {
      filterComponentData.map(item => {
        if (item?.id === id) {
          item.options.map(options => {
            options.checked = checked;
          });
        }
      });
    }
  };

  const getDefaultFilterState = () => ({
    confidenceScore: [],
    excludeTeamOwns: true,
    industry: [],
    sector: [],
    employeeCount: [],
    revenue: [],
    department: [],
    funding: [],
    fundingTotalAmountStart: null,
    fundingTotalAmountEnd: null,
    fundingLastAmountStart: null,
    fundingLastAmountEnd: null,
    fundingDurationStart: null,
    fundingDurationEnd: null,
    employeesByDepartment: [],
    growthByDepartment: [],
    companyType: [],
    foundedYearStart: null,
    foundedYearEnd: null,
    companyType: [],
    foundedYearStart: null,
    foundedYearEnd: null,
    technologies: [],
    level: [],
    title: [],
    titleExactMatch: false,
    companyDomain: [],
    companyName: [],
    companyDomainList: [],
    companyNameList: [],
    companySearchType: "domain",
    companyKeywords: [],
    contactKeywords: [],
    profileBadge: [],
    ...DEFAULT_EXPERIENCE_DURATION,
    country: [],
    state: [],
    city: [],
    companyCountry: [],
    companyState: [],
    companyCity: [],
    name: ""
  });

  const clearAllAction = () => {
    setSelectedValueId("");
    setCheckboxVal(false);
    const performClearAll = () => {
      Utils.mixpanelTrack(MXP_EVENT.CLEAR_ALL_CLICKED, {
        pageType: PAGE_TYPE.discover
      });
      dataSearchState.setClearAll(true);
      dataSearchState.setFilterComponentData(getDefaultFilterState());
      dataSearchState.setSelectedFilteredList({});
      dataSearchState.setCurrentPage(1);
      setTableLoading(true);
      dataSearchState.setExperienceDurationErrors({});
      dataSearchState.setEmployeesByDepartmentErrors({});
      dataSearchState.setGrowthByDepartmentErrors({});
      resetFilterData("level");
      resetFilterData("confidenceScore");
      dataSearchState.setIsFromSeoPage(false);
      dataSearchState.setShowInitialPage(true);
      dataSearchState.setFilterApplied(false);
      dataSearchState.setContactTableData({
        totalContacts: 0
      });
      dataSearchState.setLastSearchedFilters(null);
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performClearAll);
    } else {
      performClearAll();
    }
  };

  const getListOfSavedSearch = () => {
    const config = {
      url: URLS.getSavedSearchV2,
      method: "GET"
    };
    return makeApi(config);
  };

  const deleteSavedSearch = id => {
    const config = {
      url: URLS.deleteSearchV2,
      method: "POST",
      data: { id }
    };
    return makeApi(config);
  };

  const handleGetSearch = async () => {
    const willOpen = !showDropdown;
    if (willOpen) {
      setSavedSearchListLoader(true);
      let response = await getListOfSavedSearch();
      if (response?.status === 200) {
        const raw = response?.data;
        const list = Array.isArray(raw)
          ? raw
          : raw?.content ?? raw?.data ?? raw?.savedSearches ?? [];
        setSavedListOptions(Array.isArray(list) ? list : []);
      }
      setSavedSearchListLoader(false);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleDeleteSearch = async (id = "") => {
    let response = await deleteSavedSearch(id);
    if (response?.status === 200) {
      Utils.mixpanelTrack(MXP_EVENT.SEARCH_DELETED, {
        pageType: PAGE_TYPE.discover
      });
      toasterState.setToastMsg("Saved search deleted successfully", "success");
      let temp = savedListOptions.filter(item => item.id !== id);
      setSavedListOptions(temp);
    } else {
      toasterState.setToastMsg(
        "Deletion was unsuccussful,please try again later",
        "success"
      );
    }
    if (
      id === selectedValueId ||
      (setSavedListOptions &&
        setSavedListOptions?.length === 1 &&
        savedListOptions[0].id === id)
    ) {
      clearAllAction();
    }
    confirmationPopupState.setShowPopup(false);
    setShowDropdown(false);
  };

  const handleDeletePopup = (id = "") => {
    confirmationPopupState.setPopupValues({
      content: `Are you sure you want to delete the saved search?`,
      actionBtnText: "Confirm",
      cancelBtnText: "Cancel",
      loadingMsg: "Please wait...",
      callback: () => {
        handleDeleteSearch(id);
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  const handleEditSearch = (id = "", searchName = "") => {
    editCallBack(id, searchName);
    setShowDropdown(false);
  };

  const listOfSavedSearchBody = props => {
    const {
      searchName = "",
      createTs = "",
      id = "",
      dropDownValue = ""
    } = props;
    const createdDate = Utils.formatDate(createTs);
    let tempDropDownValue = dataSearchState.clearAll ? "" : selectedValueId;

    return (
      <>
        <div className="customListLeftSection">
          <span
            className={`${id === tempDropDownValue ? "updatedName" : "nonUpdatedName"
              }`}
          >
            {searchName}
          </span>
          <span className="createdDate">{`Created on ${createdDate}`}</span>
        </div>
        <div className="customListHorizontalDisplay">
          <span
            onClick={e => {
              Utils.preventDefaultFn(e);
              handleEditSearch(id, searchName);
            }}
          >
            <EditIcon />
          </span>
          <span
            onClick={e => {
              Utils.preventDefaultFn(e);
              handleDeletePopup(id);
            }}
          >
            <DeleteIconDiscover />
          </span>
        </div>
      </>
    );
  };

  const CustomSavedSearchLoader = () => {
    return Array.from({ length: 10 }, (value, index) => (
      <li className="dropdownOptions optionLoader" key={`${index}`}>
        <>
          <div className="customListLeftSection">
            <span
              className="updatedName linear-background"
              style={{ width: "150px" }}
            />
            <span
              className="createdDate linear-background"
              style={{ width: "150px" }}
            />
          </div>
          <div className="customListHorizontalDisplay">
            <span
              className="iconBackground linear-background"
              style={{ width: "20px" }}
            />
            <span
              className="iconBackground linear-background"
              style={{ width: "20px" }}
            />
          </div>
        </>
      </li>
    ));
  };

  const LoaderHeader = () => {
    return (
      <span
        className="filterHeaderLink"
        onClick={() => {
          handleGetSearch();
        }}
      >
        Load
      </span>
    );
  };

  const savedListCbk = item => {
    setTableLoading(true);
    const {
      confidenceScore = [],
      titleExactMatch = false,
      industry = [],
      sector = [],
      employeeCount = [],
      revenue = [],
      department = [],
      level = [],
      title = [],
      excludeTeamOwns = false,
      keywords = [],
      companyKeywords = [],
      contactKeywords = [],
      profileBadge = [],
      experienceCurrentJobMinYear,
      experienceCurrentJobMaxYear,
      experienceCurrentJobMinMonth,
      experienceCurrentJobMaxMonth,
      experienceCurrentCompanyMinYear,
      experienceCurrentCompanyMaxYear,
      experienceCurrentCompanyMinMonth,
      experienceCurrentCompanyMaxMonth,
      experienceTotalMinYear,
      experienceTotalMaxYear,
      experienceTotalMinMonth,
      experienceTotalMaxMonth,
      companyDomain: rawCompanyDomain = [],
      companyName: rawCompanyName = [],
      companyDomainList: rawCompanyDomainList = [],
      companyNameList: rawCompanyNameList = [],
      companySearchType = "domain",
      funding = [],
      fundingTotalAmountStart = null,
      fundingTotalAmountEnd = null,
      fundingLastAmountStart = null,
      fundingLastAmountEnd = null,
      fundingDurationStart = null,
      fundingDurationEnd = null,
      country = [],
      state = [],
      city = [],
      companyCountry = [],
      companyState = [],
      companyCity = [],
      name = "",
      companies = [],
      locations = [],
      companyLocations = []
    } = item;
    const companyDomain = (() => {
      const raw = rawCompanyDomain;
      if (Array.isArray(raw)) {
        return raw.map(v =>
          typeof v === "object" && v != null
            ? (v.primaryDomain || v.name || v.id || "")
            : String(v ?? "")
        ).filter(Boolean);
      }
      if (raw != null && raw !== "") return [String(raw)];
      return [];
    })();
    const companyName = (() => {
      const raw = rawCompanyName;
      if (Array.isArray(raw)) {
        return raw.map(v => (typeof v === "object" && v != null ? (v.name || v.id || "") : String(v ?? ""))).filter(Boolean);
      }
      if (raw != null && raw !== "") return [String(raw)];
      return [];
    })();
    const companyDomainList = Array.isArray(rawCompanyDomainList) ? rawCompanyDomainList.map(v => (typeof v === "object" && v != null ? String(v?.id ?? v?.name ?? "") : String(v ?? ""))).filter(Boolean) : [];
    const companyNameList = Array.isArray(rawCompanyNameList) ? rawCompanyNameList.map(v => (typeof v === "object" && v != null ? String(v?.name ?? v?.id ?? "") : String(v ?? ""))).filter(Boolean) : [];
    let filterAttr = {
      confidenceScore,
      titleExactMatch,
      industry,
      sector,
      employeeCount,
      revenue,
      department,
      level,
      title,
      excludeTeamOwns,
      companyDomain,
      companyName,
      companyDomainList,
      companyNameList,
      companySearchType: ["domain", "name", "domainList", "nameList"].includes(companySearchType) ? companySearchType : "domain",
      funding: Array.isArray(funding) ? funding : [],
      fundingTotalAmountStart: fundingTotalAmountStart != null ? fundingTotalAmountStart : null,
      fundingTotalAmountEnd: fundingTotalAmountEnd != null ? fundingTotalAmountEnd : null,
      fundingLastAmountStart: fundingLastAmountStart != null ? fundingLastAmountStart : null,
      fundingLastAmountEnd: fundingLastAmountEnd != null ? fundingLastAmountEnd : null,
      fundingDurationStart: fundingDurationStart != null && fundingDurationStart !== "" ? fundingDurationStart : null,
      fundingDurationEnd: fundingDurationEnd != null && fundingDurationEnd !== "" ? fundingDurationEnd : null,
      companyKeywords: companyKeywords?.length ? companyKeywords : (keywords?.length ? keywords : []),
      contactKeywords: contactKeywords?.length ? contactKeywords : (keywords?.length ? keywords : []),
      profileBadge: profileBadge?.length ? profileBadge : [],
      ...Object.fromEntries(
        Object.keys(DEFAULT_EXPERIENCE_DURATION).map(k => {
          const v = {
            experienceCurrentJobMinYear,
            experienceCurrentJobMaxYear,
            experienceCurrentJobMinMonth,
            experienceCurrentJobMaxMonth,
            experienceCurrentCompanyMinYear,
            experienceCurrentCompanyMaxYear,
            experienceCurrentCompanyMinMonth,
            experienceCurrentCompanyMaxMonth,
            experienceTotalMinYear,
            experienceTotalMaxYear,
            experienceTotalMinMonth,
            experienceTotalMaxMonth
          }[k];
          return [k, v != null && Number(v) >= 0 ? toNum(v) : DEFAULT_EXPERIENCE_DURATION[k]];
        })
      ),
      country,
      state,
      city,
      companyCountry,
      companyState,
      companyCity,
      name
    };
    dataSearchState.setSelectedFilteredList({
      location: [],
      companyDomain: [],
      companyLocation: [],
      companyKeywords: [],
      contactKeywords: []
    });
    dataSearchState.setFilterComponentData({
      ...filterAttr
    });
    if (dataSearchState.filterComponentData.titleExactMatch) {
      setCheckboxVal(true);
    }
    let tempCompanyArr = [];
    if (companies && companies?.length > 0) {
      for (const iterator of companies) {
        tempCompanyArr.push({ ...iterator, checked: true });
      }
    }
    let tempLocationArr = [];
    if (locations && locations?.length > 0) {
      for (const iterator of locations) {
        tempLocationArr.push({ ...iterator, checked: true });
      }
    }
    let tempCompanyLocationArr = [];
    if (companyLocations && companyLocations?.length > 0) {
      for (const iterator of companyLocations) {
        tempCompanyLocationArr.push({ ...iterator, checked: true });
      }
    }
    const toKeywordChips = (arr, id) =>
      (arr || []).map(iterator => ({
        name: typeof iterator === "string" ? iterator : iterator?.name ?? iterator,
        checked: true,
        id: typeof iterator === "string" ? iterator : iterator?.id ?? iterator,
        group: "keywords",
        searchText: Array.isArray(iterator?.searchText) ? iterator.searchText : [typeof iterator === "string" ? iterator : iterator?.name ?? iterator]
      }));
    const tempCompanyKeywordsArr = toKeywordChips(companyKeywords?.length ? companyKeywords : keywords, "companyKeywords");
    const tempContactKeywordsArr = toKeywordChips(contactKeywords?.length ? contactKeywords : keywords, "contactKeywords");
    dataSearchState.setSelectedFilteredList({
      ...dataSearchState.selectedFilteredList,
      companyDomain: tempCompanyArr,
      location: tempLocationArr,
      companyLocation: tempCompanyLocationArr,
      companyKeywords: tempCompanyKeywordsArr,
      contactKeywords: tempContactKeywordsArr
    });
    setShowDropdown(false);
    dataSearchState.setCurrentPage(1);
    dataSearchState.setIsFromSeoPage(false);
    dataSearchState.searchPageData();
  };

  return (
    <section className="filterComponentV2Wrapper">
      <div className="filterComponentHeader filterComponentHeaderLoadSave">
        <section className="customListWrapper">
          <div className="customListContainer">
            <AutoCompleteDropdown
              HeaderChildren={<LoaderHeader />}
              optionList={savedListOptions}
              cbk={savedListCbk}
              loader={savedSearchListLoader}
              updateListBody={true}
              Body={listOfSavedSearchBody}
              loaderComponent={<CustomSavedSearchLoader />}
              showDropdown={showDropdown}
              setShowDropdown={setShowDropdown}
              discoverPage={true}
              setSelectedValueCbk={setSelectedValueCbk}
              handleGetSearch={handleGetSearch}
            />
          </div>
        </section>
        <span className="filterHeaderSeparator">|</span>
        <span
          className="filterHeaderLink"
          onClick={() => {
            if (minimumSelectionMade) {
              toasterState.setToastMsg(
                "Oops! You cannot save a search without applying any filters.",
                "failure"
              );
            } else {
              setShowSavePopup(true);
            }
          }}
        >
          Save
        </span>
        <span className="filterHeaderSeparator">|</span>
        <span
          className="filterHeaderLink"
          onClick={clearAllAction}
        >
          Clear All
        </span>
      </div>

      <div className="filterComponentBody filterComponentBodyListSlider" id="startSearchingItem">
        <div className="filterListLeft">
          <div className="filterSectionHeader filterListSectionHeader">Company</div>
          {companyFilterData.map((item, index) => {
            const { name = "", id = "", icon = "" } = item;
            const count = getFilterCount(id);
            const isActive = activeFilterIndex === index;
            const performSelect = () => {
              if (isActive) {
                setActiveFilterIndex(null);
                setActiveIndex(null);
                return;
              }
              if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
                verifyBulkActionPerformedAndAllow(() => {
                  setActiveFilterIndex(index);
                  setActiveIndex(index);
                });
              } else {
                setActiveFilterIndex(index);
                setActiveIndex(index);
              }
            };
            return (
              <div
                key={`${id}_company`}
                className={`filterListItem ${isActive ? "isActive" : ""}`}
                onClick={performSelect}
                onKeyDown={e => e.key === "Enter" && performSelect()}
                role="button"
                tabIndex={0}
              >
                {icon && <span className="material-icons filterListItemIcon">{icon}</span>}
                <span className="filterListItemName">{name}</span>
                {count > 0 && <span className="filterListItemCount">{count}</span>}
              </div>
            );
          })}
          <div className="filterSectionHeader filterListSectionHeader">Contact</div>
          {contactFilterData.map((item, index) => {
            const idx = companyFilterData.length + index;
            const { name = "", id = "", icon = "" } = item;
            const count = getFilterCount(id);
            const isActive = activeFilterIndex === idx;
            const performSelect = () => {
              if (isActive) {
                setActiveFilterIndex(null);
                setActiveIndex(null);
                return;
              }
              if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
                verifyBulkActionPerformedAndAllow(() => {
                  setActiveFilterIndex(idx);
                  setActiveIndex(idx);
                });
              } else {
                setActiveFilterIndex(idx);
                setActiveIndex(idx);
              }
            };
            return (
              <div
                key={`${item.id}_contact`}
                className={`filterListItem ${isActive ? "isActive" : ""}`}
                onClick={performSelect}
                onKeyDown={e => e.key === "Enter" && performSelect()}
                role="button"
                tabIndex={0}
              >
                {icon && <span className="material-icons filterListItemIcon">{icon}</span>}
                <span className="filterListItemName">{name}</span>
                {count > 0 && <span className="filterListItemCount">{count}</span>}
              </div>
            );
          })}
        </div>

        {activeFilterIndex != null && (
        <div className="filterSliderRight isOpen">
          {(() => {
            const allFilters = [...companyFilterData, ...contactFilterData];
            const item = allFilters[activeFilterIndex];
            if (!item) return null;
            const { name = "", action = "", id = "" } = item;
            const filterCount = getFilterCount(id);
            const isDropdown = ["multiSelectDropdown", "jobFunctionDropdown", "fundingDropdown", "autoCompleteMultiSelectDropdown"].includes(action);
            return (
              <>
                <div className="filterSliderHeader">
                  <span className="filterSliderTitle">{name}</span>
                  <div className="filterSliderHeaderActions">
                    {filterCount > 0 && (
                      <button
                        type="button"
                        className="filterSliderClear"
                        onClick={() => clearSingleFilter(id)}
                        aria-label="Clear selection"
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      className="filterSliderClose"
                      onClick={() => { setActiveFilterIndex(null); setActiveIndex(null); onSliderOpenChange(false); }}
                      aria-label="Close"
                    >
                      <span className="material-icons">close</span>
                    </button>
                  </div>
                </div>
                <div className="filterSliderContent" key={id}>
                  {action === "employeesByDepartment" && <EmployeesByDepartmentInputs />}
                  {action === "growthByDepartment" && <GrowthByDepartmentInputs />}
                  {action === "foundedYear" && <FoundedYearInputs />}
                  {action === "companyDomainName" && (
                    <>
                      <FilterComponentAction
                        key={id}
                        {...item}
                        activeIndex={activeIndex}
                        index={activeFilterIndex}
                        setTableLoading={setTableLoading}
                        setActiveIndex={setActiveIndex}
                        filterCount={filterCount}
                      />
                      <AddRemoveValue
                        defaultValue={(() => {
                          const st = dataSearchState?.filterComponentData?.companySearchType || "domain";
                          if (st === "domainList" && (dataSearchState?.filterComponentData?.companyDomainList?.length > 0)) return ["Company Domain List"];
                          if (st === "nameList" && (dataSearchState?.filterComponentData?.companyNameList?.length > 0)) return ["Company Name List"];
                          if (st === "name") return [...new Set(dataSearchState?.filterComponentData?.companyName || [])];
                          return [...new Set(dataSearchState?.filterComponentData?.companyDomain || [])];
                        })()}
                        id={id}
                        listKey={(() => {
                          const st = dataSearchState?.filterComponentData?.companySearchType || "domain";
                          if (st === "domainList") return "companyDomainList";
                          if (st === "nameList") return "companyNameList";
                          return st === "name" ? "companyName" : "companyDomain";
                        })()}
                        store={dataSearchState}
                        setTableLoading={setTableLoading}
                      />
                    </>
                  )}
                  {action === "experienceDuration" && (
                    <FilterComponentAction
                      key={id}
                      {...item}
                      activeIndex={activeIndex}
                      index={activeFilterIndex}
                      setTableLoading={setTableLoading}
                      setActiveIndex={setActiveIndex}
                      filterCount={filterCount}
                    />
                  )}
                  {action === "inputBox" && (
                    <>
                      <FilterComponentAction
                        key={id}
                        {...item}
                        activeIndex={activeIndex}
                        index={activeFilterIndex}
                        setTableLoading={setTableLoading}
                        setActiveIndex={setActiveIndex}
                        filterCount={filterCount}
                      />
                      <AddRemoveValue
                        defaultValue={[...new Set(dataSearchState?.filterComponentData?.[id])] || []}
                        id={id}
                        store={dataSearchState}
                        setTableLoading={setTableLoading}
                      />
                    </>
                  )}
                  {action === "toggle" && (
                    <FilterComponentAction
                      key={id}
                      {...item}
                      activeIndex={activeIndex}
                      index={activeFilterIndex}
                      setTableLoading={setTableLoading}
                      setActiveIndex={setActiveIndex}
                    />
                  )}
                  {isDropdown && (
                    <>
                      {action === "fundingDropdown" && (
                        <div className="fundingTypeTriggerWrap">
                          <button
                            type="button"
                            className="fundingTypeTrigger"
                            onClick={() => setFundingTypeDropdownOpen(!fundingTypeDropdownOpen)}
                          >
                            <span className="fundingTypeTriggerLabel">
                              Funding type
                              {(dataSearchState?.filterComponentData?.funding || []).length > 0 && (
                                <span className="fundingTypeTriggerCount">
                                  {" "}({(dataSearchState.filterComponentData.funding || []).length} selected)
                                </span>
                              )}
                            </span>
                            <span className="material-icons fundingTypeTriggerIcon">
                              {fundingTypeDropdownOpen ? "expand_less" : "expand_more"}
                            </span>
                          </button>
                          {(dataSearchState?.filterComponentData?.funding || []).length > 0 && (
                            <div className="fundingTypeSelectedChips addRemoveValueWrapper">
                              {(dataSearchState.filterComponentData.funding || []).map(valueId => {
                                let label = valueId;
                                (FUNDING_CATEGORIES || []).forEach(cat => {
                                  const item = (cat.items || []).find(i => i.id === valueId);
                                  if (item) label = item.label;
                                });
                                return (
                                  <div className="tagItem" key={valueId}>
                                    <span className="selectedTag">{label}</span>
                                    <i
                                      className="material-icons selectedTagCloseIcon"
                                      onClick={e => {
                                        e.stopPropagation();
                                        const next = (dataSearchState.filterComponentData.funding || []).filter(id => id !== valueId);
                                        dataSearchState.setFilterComponentData({
                                          ...dataSearchState.filterComponentData,
                                          funding: next
                                        });
                                      }}
                                      role="button"
                                      aria-label={`Remove ${label}`}
                                    >
                                      close
                                    </i>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                      <FilterComponentAction
                        key={id}
                        {...item}
                        activeIndex={activeIndex}
                        index={activeFilterIndex}
                        setActiveIndex={setActiveIndex}
                        setTableLoading={setTableLoading}
                        fundingTypeDropdownOpen={action === "fundingDropdown" ? fundingTypeDropdownOpen : undefined}
                        onCloseFundingTypeDropdown={action === "fundingDropdown" ? () => setFundingTypeDropdownOpen(false) : undefined}
                      />
                      {action === "fundingDropdown" && <FundingRangeInputs />}
                      {action === "autoCompleteMultiSelectDropdown" && id?.toLowerCase() !== "industry" && (
                        <AddRemoveValue
                          defaultValue={dataSearchState?.selectedFilteredList?.[id] || []}
                          id={id}
                          store={dataSearchState}
                          setTableLoading={setTableLoading}
                        />
                      )}
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </div>
        )}
      </div>
      <div className="filterSearchBtn">
        <button
          type="button"
          className="btn btnPrimary filterSearchBtnButton"
          disabled={!dataSearchState.hasAnyFilterSelected()}
          onClick={async () => {
            if (!dataSearchState.hasAnyFilterSelected()) return;
            setActiveFilterIndex(null);
            setActiveIndex(null);
            setTableLoading(true);
            dataSearchState.setCurrentPage(1);
            dataSearchState.setIsFromSeoPage(false);
            const ran = await dataSearchState.searchPageData();
            if (!ran) setTableLoading(false);
          }}
        >
          Search
        </button>
      </div>
    </section>
  );
});

export { FilterComponent };
export default FilterComponent;
